'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { normalizeMarkdown } from '@/lib/markdown';
import { sanitizeAiText } from '@/lib/aiTextSanitize';

export interface SortableContentCardProps {
  id: string;
  title: string;
  content: string;
  updatedAt: number;
  onUpdate: (id: string, title: string, content: string) => void;
  onDelete: (id: string) => void;
  metadataSlot?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export default function SortableContentCard({
  id,
  title,
  content,
  updatedAt,
  onUpdate,
  onDelete,
  metadataSlot,
  headerActions,
}: SortableContentCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editContent, setEditContent] = useState(content);
  const [copied, setCopied] = useState(false);
  const [contentHeight, setContentHeight] = useState<number>(40);
  const titleRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setEditTitle(title);
      setEditContent(content);
    }
  }, [title, content, isEditing]);

  const saveAndClose = useCallback(() => {
    setIsEditing(false);
    if (editTitle !== title || editContent !== content) {
      onUpdate(id, editTitle, editContent);
    }
  }, [editTitle, editContent, title, content, id, onUpdate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditTitle(title);
      setEditContent(content);
      setIsEditing(false);
    }
  }, [title, content]);

  const autoResize = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    const newHeight = Math.max(el.scrollHeight, contentHeight);
    el.style.height = newHeight + 'px';
    setContentHeight(newHeight);
  }, [contentHeight]);

  useEffect(() => {
    if (!isEditing && contentRef.current) {
      const h = contentRef.current.scrollHeight;
      if (h > contentHeight) {
        setContentHeight(h);
      }
    }
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative break-inside-avoid mb-3 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-[var(--border)]">
        <button
          className="cursor-grab active:cursor-grabbing text-[var(--text-muted)] hover:text-[var(--text-secondary)] text-sm shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
        {isEditing ? (
          <input
            ref={titleRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={(e) => {
              if (!(e.currentTarget.closest('.group') as HTMLElement)?.contains(e.relatedTarget as Node)) {
                saveAndClose();
              }
            }}
            placeholder="タイトル"
            className="flex-1 min-w-0 bg-transparent text-[var(--text-primary)] text-sm font-semibold outline-none"
          />
        ) : (
          <span
            className="flex-1 min-w-0 text-sm font-semibold text-[var(--text-primary)] truncate cursor-text"
            onClick={() => setIsEditing(true)}
          >
            {title || <span className="text-[var(--text-muted)] font-normal italic">無題</span>}
          </span>
        )}
        {headerActions && (
          <div className="flex items-center gap-1 shrink-0">
            {headerActions}
          </div>
        )}
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => {
              navigator.clipboard.writeText(content);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className={`text-sm ${copied ? 'text-[var(--success)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
            title="本文をコピー"
          >
            {copied ? '✓' : '⧉'}
          </button>
          <button
            onClick={() => onDelete(id)}
            className="text-[var(--text-muted)] hover:text-[var(--danger)] text-sm"
            title="削除"
          >
            ✕
          </button>
        </div>
      </div>

      {metadataSlot && (
        <div className="px-3 py-1 border-b border-[var(--border)] bg-[var(--bg-secondary)]/30">
          {metadataSlot}
        </div>
      )}

      <div
        className="px-3 py-2 cursor-text"
        style={{ minHeight: contentHeight + 16 }}
        onClick={() => !isEditing && setIsEditing(true)}
        onBlur={(e) => {
          if (!e.currentTarget.closest('.group')?.contains(e.relatedTarget as Node)) {
            saveAndClose();
          }
        }}
      >
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={editContent}
            onChange={(e) => {
              setEditContent(e.target.value);
              autoResize(e.target);
            }}
            onKeyDown={handleKeyDown}
            className="w-full bg-transparent text-[var(--text-primary)] resize-none outline-none"
            style={{
              height: contentHeight,
              minHeight: contentHeight,
              fontSize: 'var(--card-font-size, 0.875rem)',
            }}
            placeholder="メモを入力..."
            onFocus={(e) => autoResize(e.target)}
          />
        ) : (
          <div ref={contentRef} style={{ minHeight: contentHeight }}>
            {content ? (
              <div
                className="prose prose-sm max-w-none break-words text-[var(--text-primary)] prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)] prose-code:text-[var(--text-primary)] prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-pre:my-2"
                style={{ fontSize: 'var(--card-font-size, 0.875rem)' }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {normalizeMarkdown(sanitizeAiText(content))}
                </ReactMarkdown>
              </div>
            ) : (
              <p
                className="text-[var(--text-primary)] whitespace-pre-wrap break-words"
                style={{ fontSize: 'var(--card-font-size, 0.875rem)' }}
              >
                <span className="text-[var(--text-muted)] italic">クリックして入力...</span>
              </p>
            )}
          </div>
        )}
      </div>

      <div className="px-3 pb-1.5 text-[10px] text-[var(--text-muted)]">
        {new Date(updatedAt).toLocaleDateString('ja-JP', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
}
